import { useState, useRef, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import ProductCard from '@/components/ProductCard';
import { Ban, Filter, LoaderCircle, X } from 'lucide-react';
import { ProductLine, ProductType, SkinType } from '../types/enum';
import { useQuery } from '@apollo/client';
import { PRODUCTS } from '../lib/graphql/queries/product';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const PAGE_SIZE = 10;

// Filter content extracted for reuse
const FilterContent = ({
  skinTypeParam,
  productLineParam,
  productTypeParam,
  activeFiltersCount,
  clearAllFilters,
  updateFilter,
  formatTitle,
}: {
  skinTypeParam: string | null;
  productLineParam: string | null;
  productTypeParam: string | null;
  activeFiltersCount: number;
  clearAllFilters: () => void;
  updateFilter: (key: string, value: string | null) => void;
  formatTitle: (str: string | null) => string;
}) => (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <h3 className="font-semibold text-sm uppercase tracking-wide">Шүүлтүүр</h3>
      {activeFiltersCount > 0 && (
        <button
          onClick={clearAllFilters}
          className="text-sm text-primary hover:underline flex items-center gap-1"
        >
          <X className="w-4 h-4" />
          Цэвэрлэх
        </button>
      )}
    </div>

    {/* Skin Type */}
    <div className="space-y-3">
      <h4 className="font-medium text-sm">Арьсны төрөл</h4>
      {Object.values(SkinType).map((type) => (
        <label key={type} className="flex items-center gap-3 cursor-pointer">
          <input
            type="radio"
            name="skinType"
            checked={skinTypeParam?.toUpperCase() === type}
            onChange={() =>
              updateFilter('skinType', skinTypeParam?.toUpperCase() === type ? null : type)
            }
            className="w-4 h-4 text-primary focus:ring-primary"
          />
          <span className="text-sm">{formatTitle(type)}</span>
        </label>
      ))}
    </div>

    <hr className="border-border" />

    {/* Product Line */}
    <div className="space-y-3">
      <h4 className="font-medium text-sm">Ангилал</h4>
      {Object.values(ProductLine).map((line) => (
        <label key={line} className="flex items-center gap-3 cursor-pointer">
          <input
            type="radio"
            name="productLine"
            checked={productLineParam?.toUpperCase() === line}
            onChange={() =>
              updateFilter('productLine', productLineParam?.toUpperCase() === line ? null : line)
            }
            className="w-4 h-4 text-primary focus:ring-primary"
          />
          <span className="text-sm">{formatTitle(line)}</span>
        </label>
      ))}
    </div>

    <hr className="border-border" />

    {/* Product Type */}
    <div className="space-y-3">
      <h4 className="font-medium text-sm">Бүтээгдэхүүний төрөл</h4>
      {Object.values(ProductType).map((type) => (
        <label key={type} className="flex items-center gap-3 cursor-pointer">
          <input
            type="radio"
            name="productType"
            checked={productTypeParam?.toUpperCase() === type}
            onChange={() =>
              updateFilter('productType', productTypeParam?.toUpperCase() === type ? null : type)
            }
            className="w-4 h-4 text-primary focus:ring-primary"
          />
          <span className="text-sm">{formatTitle(type)}</span>
        </label>
      ))}
    </div>
  </div>
);

const Shop = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterOpen, setFilterOpen] = useState(false);

  const skinTypeParam = searchParams.get('skinType');
  const productLineParam = searchParams.get('productLine');
  const productTypeParam = searchParams.get('productType');
  const searchParam = searchParams.get('search')?.trim() || '';
  const sortParam = searchParams.get('sort') || 'createdAt_DESC';

  const [page, setPage] = useState(1);
  const [products, setProducts] = useState<any[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [sortField, sortOrder] = sortParam.split('_') as [
    'createdAt' | 'price',
    'ASC' | 'DESC'
  ];

  const skinType = skinTypeParam
    ? (Object.values(SkinType) as string[]).find(
        (t) => t.toLowerCase() === skinTypeParam.toLowerCase()
      ) || null
    : null;

  const productLine = productLineParam
    ? (Object.values(ProductLine) as string[]).find(
        (l) => l.toLowerCase() === productLineParam.toLowerCase()
      ) || null
    : null;

  const productType = productTypeParam
    ? (Object.values(ProductType) as string[]).find(
        (t) => t.toLowerCase() === productTypeParam.toLowerCase()
      ) || null
    : null;

  const variables = {
    searchValue: searchParam,
    newArrival: false,
    page,
    limit: PAGE_SIZE,
    skinType,
    productLine,
    productType,
    sort: { field: sortField, order: sortOrder },
  };

  const { data, loading } = useQuery(PRODUCTS, {
    variables,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (data?.publicProducts) {
      if (page === 1) {
        setProducts(data.publicProducts);
      } else {
        setProducts((prev) => [...prev, ...data.publicProducts]);
      }
      setHasMore(data.publicProducts.length === PAGE_SIZE);
    }
  }, [data, page]);

  useEffect(() => {
    setPage(1);
    setProducts([]);
    setHasMore(true);
  }, [skinTypeParam, productLineParam, productTypeParam, searchParam, sortParam]);

  const handleSortChange = (value: string) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set('sort', value);
      newParams.delete('page');
      return newParams;
    });
  };

  const observer = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          setPage((prev) => prev + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  const updateFilter = (key: string, value: string | null) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
      newParams.delete('page');
      return newParams;
    });
  };

  const clearAllFilters = () => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.delete('skinType');
      newParams.delete('productLine');
      newParams.delete('productType');
      newParams.delete('page');
      return newParams;
    });
  };

  const formatTitle = (str: string | null) =>
    str
      ? str
          .replace(/_/g, ' ')
          .split(' ')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ')
      : '';

  const activeFiltersCount =
    (skinTypeParam ? 1 : 0) + (productLineParam ? 1 : 0) + (productTypeParam ? 1 : 0);

  const filterProps = {
    skinTypeParam,
    productLineParam,
    productTypeParam,
    activeFiltersCount,
    clearAllFilters,
    updateFilter,
    formatTitle,
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6 md:mb-8">
        <h1 className="text-2xl md:text-4xl font-light mb-2">
          {skinTypeParam && `${formatTitle(skinTypeParam)} `}
          {productLineParam && `${formatTitle(productLineParam)} `}
          {productTypeParam && `${formatTitle(productTypeParam)} `}
          {!skinTypeParam && !productLineParam && !productTypeParam && 'Бүх Бүтээгдэхүүн'}
        </h1>
        {searchParam && (
          <p className="mb-2 text-sm text-muted-foreground">
            &quot;{searchParam}&quot; хайлтын үр дүн
          </p>
        )}
        <div className="w-32 h-1 bg-primary" />
      </div>

      {/* Mobile Filter Button */}
      <div className="lg:hidden mb-4 flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setFilterOpen(true)}
          className="flex items-center gap-2"
        >
          <Filter className="w-4 h-4" />
          Шүүлтүүр
          {activeFiltersCount > 0 && (
            <Badge variant="default" className="ml-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs">
              {activeFiltersCount}
            </Badge>
          )}
        </Button>
      </div>

      {/* Mobile Filter Sheet */}
      <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
        <SheetContent side="left" className="w-[300px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Шүүлтүүр</SheetTitle>
          </SheetHeader>
          <div className="mt-4">
            <FilterContent {...filterProps} />
          </div>
        </SheetContent>
      </Sheet>

      <div className="grid lg:grid-cols-[280px_1fr] gap-8">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:block space-y-6">
          <div className="bg-background border border-border rounded-sm p-6">
            <FilterContent {...filterProps} />
          </div>
        </aside>

        {/* Main Content */}
        <div>
          <div className="mb-6 flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Нийт <strong>{products.length}</strong> бүтээгдэхүүн олдлоо
            </p>
            <select
              value={sortParam}
              onChange={(e) => handleSortChange(e.target.value)}
              className="text-sm border border-border rounded-sm px-3 py-2 bg-background"
            >
              <option value="createdAt_DESC">Шинэ нь эхэндээ</option>
              <option value="price_ASC">Үнэ: Өсөхөөр</option>
              <option value="price_DESC">Үнэ: Буурахаар</option>
            </select>
          </div>

          {products.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
              {products.map((product) => (
                <div className="relative" key={product.id}>
                  <ProductCard {...product} />
                  {product.status === 'outOfStock' && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-60 z-10">
                      <span className="text-white text-lg font-bold">Дууссан</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            !loading && (
              <div className="text-center py-20 text-muted-foreground">
                <Ban className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Бүтээгдэхүүн олдсонгүй</p>
              </div>
            )
          )}

          {hasMore && (
            <div ref={loadMoreRef} className="flex justify-center py-12">
              {loading && page > 1 && (
                <LoaderCircle className="w-10 h-10 animate-spin text-primary" />
              )}
            </div>
          )}

          {!hasMore && products.length > 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <p>Бүх бүтээгдэхүүнийг үзсэн байна</p>
            </div>
          )}

          {loading && page === 1 && (
            <div className="flex justify-center py-20">
              <LoaderCircle className="w-12 h-12 animate-spin text-primary" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Shop;
